import React, { useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import PropTypes from 'prop-types';
import './styles/SecretsInput.scss';

/**
 * Input field for secrets that guards against password autocompletion by browsers and password managers.
 *
 * This component is designed to be rendered as a form control in a bootstrap form, and is meant to be used
 * interchangeably with <input type="text"/>, but requires an `onCancel` property to reset the value.
 *
 * The use of `key` populated with the initial value is recommended. While not required, this nicely resets the
 * component when the form is saved.
 *
 * These strategies are used to avoid autocompletion:
 *  1. Use `<input type="text"/>`, not `<input type="password"/>`
 *  2. Use prop autoComplete="new-password"
 *  3. Do not render `<input/>` when secret is not being edited
 *
 * @example
 *   const initialValue = model.mySecretValue;
 *   const [value, setValue] = useState(initialValue);
 *
 *   <SecretsInput
 *     key={initialValue}
 *     value={value}
 *     onChange={e => setValue(e.target.value)}
 *     onCancel={() => setValue(initialValue)}
 *   />
 *
 * @param onCancel {function} invoked with input element as its arg when cancel button is clicked
 * @param props {object} props passed to <input/>
 * @return {JSX.Element}
 * @constructor
 */
const SecretsInput = ({ onCancel, ...props }) => {
  const { className, value } = props;
  const [initialValue] = useState(value);
  const [editing, setEditing] = useState(!initialValue);
  const inputRef = useRef(null);

  const onClick = () => {
    if (editing) {
      onCancel(inputRef.current);
    }
    setEditing(!editing);
  };

  return (
    <div className="secrets-input input-group">
      {editing ?
        <input ref={inputRef} type="text" autoComplete="new-password" {...props}/> :
        <span className={className}>{'●'.repeat(20)}</span>}

      {!!initialValue && <Button variant="secondary" onClick={onClick}>
        {editing ? 'Cancel' : 'Edit'}
      </Button>}
    </div>
  );
};

SecretsInput.defaultProps = {
  className: 'form-control form-control-sm',
  onCancel: () => console.error('cancel clicked but `onCancel` prop is not defined')
};

SecretsInput.propTypes = {
  className: PropTypes.string,
  onCancel: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  name: PropTypes.string,
  value: PropTypes.string
};

export default SecretsInput;
